import React, { useEffect, Fragment, } from 'react';
import { isEqual, sortBy, orderBy, maxBy, isEmpty, sumBy, get, keyBy } from 'lodash';
import fileDownload from 'js-file-download';
import { startOfDay, format as formatDate, addMonths, startOfMonth, endOfMonth } from 'date-fns';
import { Button, Input, } from 'reactstrap';
import numeral from 'numeral';
import { useAsync, useList, } from 'react-use';
import { toast } from 'react-toastify';

import { fetchPdf, fullPathWithParams } from '../../util';
import { batch, } from '../../shared/firebase';
import firebase, { functions } from '../../firebase';
import { prefectures } from '../../shared/config';
import { destinationFields, conclusiveReferralFee } from '../../shared/models/order';
import { fields as paymentAdjustmentFields } from '../../shared/models/paymentAdjustment';
import { fieldDisplayValue } from '../../shared/util';
import AgentPage from '../hocs/AgentPage';
import AgentHeader from '../AgentHeader';
import useDocumentsFetch from '../hooks/useDocumentsFetch';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useCollectionSubscriptionInTenant from '../hooks/useCollectionSubscriptionInTenant';
import useDocumentSubscription from '../hooks/useDocumentSubscription';
import useQueryParams from '../hooks/useQueryParams';
import ExportButton from '../ExportButton';
import ProgressButton from '../ProgressButton';
import ModalButton from '../ModalButton';
import QueryDateSelector from '../QueryDateSelector';
import QuerySelector from '../QuerySelector';
import PaymentStatementContent from '../PaymentStatementContent';
import AddInTenantButton from '../AddInTenantButton';
import EditButton from '../EditButton';
import DeleteButton from '../DeleteButton';
import ModelFormModal from '../modals/ModelFormModal';

const { entries } = Object;
const db = firebase.firestore();
const productsRef = db.collection('products');
const productTypesRef = db.collection('productTypes');
const couponsRef = db.collection('coupons');
const storageRef = firebase.storage().ref();
const getAgentOrders = functions.httpsCallable('getAgentOrders');
const sendPaymentStatement = functions.httpsCallable('sendPaymentStatement');
const dateTypes = {
  shippedDate: {
    label: '出荷日',
  },
  paymentDate: {
    label: '支払日',
  },
};
const dateTypeOptions = entries(dateTypes).map(([k, v]) => ({ label: v.label, value: k }));

export default AgentPage(function AgentReferralOrders(props) {
  const { user, agent, location, history, setBreadNavValues, isAdmin, canEditMembers, } = props;
  const now = new Date();
  const queryParams = useQueryParams();
  const {
    dateType = ['shippedDate'],
    startOn: startOnString,
    endOn: endOnString,
    month = formatDate(addMonths(new Date(), -1), 'yyyy-MM'),
    agentShops: agentShopsForFilter,
    productTypes: productTypesForFilter,
  } = queryParams;
  const monthDate = endOfMonth((_ => new Date(_[0], _[1] - 1, 1))(month.split('-').map(_ => parseInt(_, 10))));
  const [selectedIds, { push: selectId, removeAt: removeSelectedIdAt, set: setSelectedId, }] = useList([]);
  const unselectId = _ => removeSelectedIdAt(selectedIds.indexOf(_));
  const isSelecting = selectedIds.length > 0;
  const agentShops = useCollectionSubscription(agent.ref.collection('agentShops').orderBy('createdAt'), [agent]);
  const agentShopOptions = agentShops.map((_) => ({ label: _.name, value: _.id }));
  const products = useCollectionSubscriptionInTenant(productsRef.orderBy('code'));
  const productsById = keyBy(products, 'id');
  const productTypes = useCollectionSubscriptionInTenant(productTypesRef.orderBy('index'));
  const productTypeOptions = productTypes.map((_) => ({ label: _.name, value: _.id }));
  const { loading: isLoading = false, value: orders } = useAsync(async () => {
    const { data: orders } = await getAgentOrders({
      type: 'referral',
      agentId: agent.id,
      ...(
        dateType?.[0] === 'paymentDate' ? {
          startOn: formatDate(monthDate, 'yyyy-MM-dd'),
          endOn: formatDate(monthDate, 'yyyy-MM-dd'),
        } : {
          startOn: startOnString,
          endOn: endOnString,
        }
      ),
      dateType: dateType?.[0],
    });
    return orders.map(_ => ({ ..._, ref: db.collection('orders').doc(_.id), }));
  }, [dateType?.[0], startOnString, endOnString, month]);
  const ordersById = keyBy(orders, 'id');
  let paymentAdjustmentsRef = agent.ref.collection('paymentAdjustments');
  if(monthDate) {
    paymentAdjustmentsRef = paymentAdjustmentsRef
      .where('paymentDate', '>=', startOfMonth(monthDate))
      .where('paymentDate', '<=', endOfMonth(monthDate));
  }
  const paymentAdjustments = useCollectionSubscription(paymentAdjustmentsRef, [agent, startOnString, endOnString, month]);
  const coupons = useDocumentsFetch((orders || []).map(_ => _.couponId).filter(_ => _).map(_ => couponsRef.doc(_)), [orders]);
  const couponsById = keyBy(coupons, 'id');
  const rows = orderBy(
    (orders || [])
      .filter((_) => _.shippedDate && conclusiveReferralFee(productsById, _))
      .map((order) => {
        const { orderItems = [], referrerKey, couponId } = order;
        const orderItemsWithProduct = orderItems.map((orderItem) => {
          const { productId } = orderItem;
          const product = productsById[productId];
          return { ...orderItem, product, order };
        });
        const coupon = couponsById[couponId];
        return {
          order: {
            ...order,
            createdAt: new Date(JSON.parse(order.createdAt)),
            cancelledAt: order.cancelledAt && new Date(JSON.parse(order.cancelledAt)),
            paymentDate: order.paymentDate && new Date(JSON.parse(order.paymentDate)),
          },
          coupon,
          orderItemsWithProduct,
        };
      }),
    'order.createdAt',
    'desc'
  );

  let filteredRows = rows;
  if (!isEmpty(agentShopsForFilter)) {
    filteredRows = filteredRows.filter((_) => agentShopsForFilter.includes(get(_, 'order.agentShop.id')));
  }
  if (!isEmpty(productTypesForFilter)) {
    filteredRows = filteredRows.filter((_) =>
      _.orderItemsWithProduct.some((_) =>
        get(_, 'product.productTypeIds', []).some((_) => productTypesForFilter.includes(_))
      )
    );
  }

  const rowsForExport = async () => {
    const maxLengthOrderItemsRow = maxBy(filteredRows, (_) => _.orderItemsWithProduct.length);
    return filteredRows.map(({ order, orderItemsWithProduct, coupon }) => {
      const {
        id,
        cammacsStatus,
        createdBy,
        createdAt,
        shippedDate,
        cancelledAt,
        amount,
        discountAmount = 0,
        shipmentFee = 0,
        isPreOrder = false,
        name,
        prefecture,
        city,
        agentShop,
        referrer,
      } = order;
      return {
        id,
        agentShopName: agentShop && agentShop.name,
        referrerName: referrer && referrer.name,
        couponId: coupon && coupon.id,
        couponName: coupon && coupon.name,
        totalAmount: amount - discountAmount,
        discountAmount,
        fee: conclusiveReferralFee(productsById, order),
        createdAt: formatDate(createdAt, 'yyyy/MM/dd HH:mm:ss'),
        shippedDate,
        cancelledAt: cancelledAt && formatDate(cancelledAt, 'yyyy/MM/dd HH:mm:ss'),
        name,
        prefecture: prefectures[prefecture],
        city,
        ...entries(destinationFields()).reduce((x, [fieldName, fieldSettings]) => {
          return {
            ...x,
            [fieldName]: fieldDisplayValue(order[fieldName], fieldSettings),
          };
        }, {}),
        ...maxLengthOrderItemsRow.orderItemsWithProduct.reduce((x, _, i) => {
          const orderItem = orderItemsWithProduct[i];
          return {
            ...x,
            [`orderItem_${i}_productId`]: orderItem && orderItem.product.id,
            [`orderItem_${i}_productCode`]: orderItem && orderItem.product.code,
            [`orderItem_${i}_productName`]: orderItem && orderItem.product.name,
            [`orderItem_${i}_quantity`]: orderItem && orderItem.quantity,
          };
        }, {}),
      };
    });
  };
  const uncancelledRows = filteredRows.filter((_) => _.order.cancelledAt == null);
  const isAllSelected = isEqual(...[selectedIds, filteredRows.map(_ => _.order.id)].map(_ => sortBy(_, _ => _)));
  const onClickDateButton = ([startOn, endOn]) => {
    const path = fullPathWithParams(
      { startOn: formatDate(startOn, 'yyyy-MM-dd'), endOn: formatDate(endOn, 'yyyy-MM-dd') },
      location
    );
    history.replace(encodeURI(path));
  };
  const paymentStatementRows = sortBy([
    ...rows.map((row) => {
      return {
        date: row.order.shippedDate,
        amount: conclusiveReferralFee(productsById, row.order),
        description: 'へんしんバイク紹介料',
        orderId: row.order.id,
      };
    }),
    ...paymentAdjustments.map((paymentAdjustment) => {
      return {
        date: formatDate(paymentAdjustment.date.toDate(), 'yyyy/MM/dd'),
        amount: paymentAdjustment.amount,
        description: paymentAdjustment.description,
        orderId: null,
      };
    }),
  ], 'date');
  useEffect(() => {
    setBreadNavValues({ agent });
  }, [agent]);

  return (
    <div>
      <div className="agent-orders container-fluid py-5 position-relative">
        <AgentHeader activeTab="referralOrders" agent={agent} canEditMembers={canEditMembers} />
        <div className="bg-white p-4">
          <div className="d-flex align-items-end gap-1">
            {
              isAdmin && (
                <QuerySelector
                  paramName="dateType"
                  width={150}
                  options={dateTypeOptions}
                  label="日付種別"
                  disabled={isSelecting}
                  defaultValue={['shippedDate']}
                />
              )
            }
            {
              ({
                shippedDate: (
                  <Fragment>
                    <Button onClick={onClickDateButton.bind(null, [startOfMonth(now), endOfMonth(now)])} disabled={isSelecting}>
                      今月
                    </Button>
                    <Button
                      onClick={onClickDateButton.bind(null, [startOfMonth(addMonths(now, -1)), endOfMonth(addMonths(now, -1))])}
                      disabled={isSelecting}
                    >
                      先月
                    </Button>
                    <QueryDateSelector
                      paramName="startOn"
                      label="開始日"
                      history={history}
                      location={location}
                      disabled={isSelecting}
                    />
                    <QueryDateSelector
                      paramName="endOn"
                      label="終了日"
                      history={history}
                      location={location}
                      disabled={isSelecting}
                    />
                  </Fragment>
                ),
                paymentDate: (
                  <Fragment>
                    <QueryDateSelector
                      label="年月"
                      dateFormat={'yyyy年MM月末'}
                      defaultValue={addMonths(new Date(), -1)}
                      paramName="month"
                      history={history}
                      location={location}
                      disabled={isSelecting}
                      pickerProps={{ showMonthYearPicker: true, showFullMonthYearPicker: true, }}
                    />
                  </Fragment>
                ),
              })[dateType]
            }
            <QuerySelector
              paramName="agentShops"
              width={250}
              isMulti
              options={agentShopOptions}
              label="店舗で絞込み"
              disabled={isSelecting}
            />
            <QuerySelector
              paramName="productTypes"
              width={250}
              isMulti
              options={productTypeOptions}
              label="商品種別で絞込み"
              disabled={isSelecting}
            />
          </div>
          {
            isSelecting && (
              <div className="d-flex my-2 alert alert-info justify-content-start gap-1 align-items-center">
                <div>{selectedIds.length}件を選択中</div>
              </div>
            )
          }
          <div className="mt-2 d-flex justify-content-between align-items-end">
            <div>
              <span>注文件数: {filteredRows.length}</span>
              <span className="ml-3">
                注文商品数: {sumBy(filteredRows.map((_) => _.order.orderItems || []).flat(), 'quantity')}
              </span>
              <span className="ml-3">
                注文合計(キャンセル除く): &yen;{' '}
                {numeral(sumBy(uncancelledRows, (_) => _.order.amount - (_.order.discountAmount || 0))).format()}
              </span>
              <span className="ml-3">
                紹介料合計(キャンセル除く): &yen;{' '}
                {numeral(sumBy(uncancelledRows, (_) => conclusiveReferralFee(productsById, _.order))).format()}
              </span>
            </div>
            <div className="d-flex gap-1">
              {
                isAdmin && (
                  <Fragment>
                    {
                      dateType?.[0] === 'paymentDate' && !isLoading && (
                        <ModalButton title="支払明細書" modalProps={{ style: { minWidth: 1260, } }} content={_ => <PaymentStatementModalContent {...{ agent, rows: paymentStatementRows, productsById, ..._, paymentDate: monthDate, }} />} isInitiallyOpen={queryParams.showsPaymentStatementModal === '1'}>
                          支払明細書
                        </ModalButton>
                      )
                    }
                    <ModalButton title="支払調整" modalProps={{ style: { minWidth: 1260, } }} content={_ => <PaymentAdjustmentsModalContent {...{ agent, user, isAdmin, orders: selectedIds.map(_ => ordersById[_]), productsById, ..._, }} />}>
                      支払調整管理
                    </ModalButton>
                  </Fragment>
                )
              }
              <ExportButton fileName="注文.csv" rows={rowsForExport} user={user} />
            </div>
          </div>
          <div className="overflow-auto mt-2">
            {filteredRows.length > 0 ? (
              <table className="table">
                <thead className="thead-light text-center text-nowrap">
                  <tr>
                    {
                      false && (
                        <th>
                          <Input
                            type='checkbox'
                            className='position-relative m-0'
                            checked={isAllSelected}
                            onChange={_ => isAllSelected ? setSelectedId([]) : setSelectedId(filteredRows.map(_ => _.order.id))}
                          />
                        </th>
                      )
                    }
                    <th>注文ID</th>
                    <th>店舗</th>
                    <th>リファラ</th>
                    <th>優待</th>
                    <th style={{ minWidth: 300 }}>商品</th>
                    <th>合計金額(税込)</th>
                    <th>割引額(税込)</th>
                    <th>紹介料(税込)</th>
                    <th style={{ minWidth: 150 }}>支払日</th>
                    <th>注文日時</th>
                    <th>出荷日</th>
                    <th>キャンセル日時</th>
                    <th>注文者お名前</th>
                    <th>注文者都道府県</th>
                    <th>注文者市区町村</th>
                    {entries(destinationFields()).map(([fieldName, { label }]) => {
                      return <th key={fieldName}>配送先{label}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map(({ order, coupon, orderItemsWithProduct }) => {
                    const {
                      id,
                      referrer,
                      agentShop,
                      amount,
                      discountAmount = 0,
                      createdAt,
                      shippedDate,
                      name,
                      prefecture,
                      city,
                      cancelledAt,
                      paymentStatementDownloadedAt,
                      paymentDate,
                    } = order;

                    return (
                      <tr key={id}>
                        {
                          false && (
                            <td>
                              <Input
                                type='checkbox'
                                className='position-relative m-0'
                                checked={selectedIds.includes(id)}
                                onChange={(_) => (selectedIds.includes(id) ? unselectId(id) : selectId(id))}
                              />
                            </td>
                          )
                        }
                        <td>{id}</td>
                        <td>{agentShop && agentShop.name}</td>
                        <td>{referrer && referrer.name}</td>
                        <td>{coupon && coupon.name}</td>
                        <td>
                          {orderItemsWithProduct.map((orderItem) => {
                            const { product, quantity } = orderItem;
                            return (
                              product != null && (
                                <div key={product.code}>
                                  <span>{product.code}</span>
                                  <span className="ml-2">{product.name}</span>
                                  <span className="ml-2">{numeral(quantity).format('0,0')}個</span>
                                </div>
                              )
                            );
                          })}
                        </td>
                        <td className="text-right">{numeral(amount - discountAmount).format('0,0')}</td>
                        <td className="text-right">{numeral(discountAmount).format('0,0')}</td>
                        <td className="text-right">
                          {numeral(conclusiveReferralFee(productsById, order)).format('0,0')}
                        </td>
                        <td>{paymentDate != null && formatDate(paymentDate, 'yyyy年MM月末')}</td>
                        <td>{formatDate(createdAt, 'yyyy/MM/dd HH:mm')}</td>
                        <td>{shippedDate}</td>
                        <td>{cancelledAt != null && formatDate(cancelledAt, 'yyyy/MM/dd HH:mm')}</td>
                        <td>{name}</td>
                        <td>{prefectures[prefecture]}</td>
                        <td>{city}</td>
                        {entries(destinationFields()).map(([fieldName, fieldSettings]) => {
                          return <td key={fieldName}>{fieldDisplayValue(order[fieldName], fieldSettings)}</td>;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : isLoading ? (
              <div>
                <span className="fas fa-spin fa-spinner" />
              </div>
            ) : (
              <div>No Data</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

function PaymentStatementModalContent (props) {
  const { agent, rows, productsById, toggleModal, paymentDate, } = props;
  const paymentDateKey = formatDate(paymentDate, 'yyyy-MM-dd');
  const paymentStatementRef = agent.ref.collection('paymentStatements').doc(paymentDateKey);
  const paymentStatement = useDocumentSubscription(paymentStatementRef);
  const note = paymentStatement?.note || '';
  const createPdf = async () => {
    const pageNode = document.querySelector('html').cloneNode(true);
    pageNode.querySelector('body').innerHTML = document.querySelector('.payment-statement-container').innerHTML;
    return await fetchPdf(pageNode.outerHTML, { printBackground: true });
  };
  const onClickDownload = async () => {
    try {
      const pdf = await createPdf();
      fileDownload(pdf, `支払明細書.pdf`);
    } catch (e) {
      console.error(e);
      toast.error('失敗しました');
    }
  };
  const onClickSend = async () => {
    if (!window.confirm('支払明細書を送信しますか？')) {
      return;
    }
    
    try {
      const pdf = await createPdf();
      const filename = `payment-statement-${agent.id}-${paymentDateKey}.pdf`;
      const fileRef = storageRef.child(`paymentStatements/${filename}`);
      await fileRef.put(pdf, { contentType: 'application/pdf' });
      const downloadURL = await fileRef.getDownloadURL();
      
      await sendPaymentStatement({
        agentId: agent.id,
        paymentDate: paymentDate.toISOString(),
        pdfUrl: downloadURL,
        recipientEmail: agent.paymentStatementTo
      });
      
      toast.success('送信しました');
    } catch (e) {
      console.error(e);
      toast.error('送信に失敗しました');
    }
  };

  return (
    <div>
      <div className="mb-1 d-flex justify-content-end gap-1">
        <EditButton
          itemRef={paymentStatementRef}
          FormModal={ModelFormModal}
          formProps={{
            title: '備考編集',
            fields: () => ({
              note: { label: '備考', type: 'text', rows: 5 }
            })
          }}
          type="set"
        >
          備考編集
        </EditButton>
        <ProgressButton process={onClickDownload}>
          発行
        </ProgressButton>
        <ProgressButton process={onClickSend} disabled={!agent.paymentStatementTo}>
          送信
        </ProgressButton>
      </div>
      <div className="d-flex justify-content-center payment-statement-container">
        <div className="d-flex justify-content-center p-4" style={{ width: 1260, }}>
          <PaymentStatementContent {...{ agent, rows, productsById, paymentDate, note, }} />
        </div>
      </div>
    </div>
  );
}

function PaymentAdjustmentsModalContent(props) {
  const { agent, user = {}, isAdmin } = props;
  const paymentAdjustments = useCollectionSubscription(agent.ref.collection('paymentAdjustments'), [agent]);

  // dateの締め支払日（翌月末）に注文・調整が既にあれば「締め済み」とみなし、その支払日をpaymentDateとして引き継ぐ
  // （null=未締め。次回の自動締めジョブに拾わせる）
  const resolvePaymentDate = async (date) => {
    if (date == null) return null;
    const expectedPaymentDate = startOfDay(endOfMonth(addMonths(date, 1)));
    const hasClosedAdjustment = paymentAdjustments.some(
      (_) => _.paymentDate != null && _.paymentDate.toDate().getTime() === expectedPaymentDate.getTime()
    );
    if (hasClosedAdjustment) return expectedPaymentDate;
    const { data: closedOrders } = await getAgentOrders({
      type: 'referral',
      agentId: agent.id,
      dateType: 'paymentDate',
      startOn: formatDate(expectedPaymentDate, 'yyyy-MM-dd'),
      endOn: formatDate(expectedPaymentDate, 'yyyy-MM-dd'),
    });
    return closedOrders.length > 0 ? expectedPaymentDate : null;
  };
  // validateOnSubmit → processValues/beforeSubmit の順で呼ばれるため、判定結果をキャッシュして二重取得を防ぐ
  let resolvedPaymentDate = null;
  const confirmIfClosed = async ({ date }) => {
    resolvedPaymentDate = await resolvePaymentDate(date);
    if (resolvedPaymentDate == null) return true;
    return window.confirm(
      `${formatDate(resolvedPaymentDate, 'yyyy年MM月末')}支払分はすでに締め済みです。追加すると支払明細書の再送信が必要になります。よろしいですか？`
    );
  };
  const processAdjustmentValues = ({ date, ...rest }) => {
    return { ...rest, date: startOfDay(date), paymentDate: resolvedPaymentDate };
  };

  return (
    <div>
      <div className="d-flex justify-content-end mb-3">
        <AddInTenantButton
          itemRef={agent.ref.collection('paymentAdjustments').doc()}
          FormModal={ModelFormModal}
          formProps={{ title: '支払調整追加', fields: paymentAdjustmentFields() }}
          disabled={!isAdmin}
          validateOnSubmit={confirmIfClosed}
          processValues={processAdjustmentValues}
        />
      </div>
      <table className="table table-bordered">
        <thead className="thead-light text-center">
          <tr>
            <th style={{ minWidth: 200 }}>日付</th>
            <th style={{ minWidth: 150 }}>金額</th>
            <th style={{ minWidth: 150 }}>支払日</th>
            <th style={{ minWidth: 300 }}>説明</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {paymentAdjustments.map((paymentAdjustment) => {
            const { id, ref, date, amount, description, paymentDate, } = paymentAdjustment;
            return (
              <tr key={id} data-id={id}>
                <td>{formatDate(date.toDate(), 'yyyy/MM/dd')}</td>
                <td className="text-right">{numeral(amount).format('0,0')}</td>
                <td>{paymentDate && formatDate(paymentDate.toDate(), 'yyyy年MM月末')}</td>
                <td style={{ whiteSpace: 'pre-line', }}>{description}</td>
                <td className="text-nowrap text-right">
                  <EditButton
                    itemRef={ref}
                    className="ml-1"
                    FormModal={ModelFormModal}
                    formProps={{ documentName: 'paymentAdjustment', title: '支払調整編集', fields: paymentAdjustmentFields() }}
                    beforeEdit={_ => ({ ..._, date: date.toDate() })}
                    validateOnSubmit={confirmIfClosed}
                    beforeSubmit={processAdjustmentValues}
                    disabled={paymentDate != null}
                  />
                  <DeleteButton itemRef={ref} className="ml-1" disabled={paymentDate != null} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
